import { useState, useMemo, useCallback, useRef } from "react";
import { FullPageScreen } from "@/lib/Components/Screens/FullPageScreen";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  ReceivablesReconciliationListItem,
  receivablesReconciliationQuery,
  receivablesReconciliationSummaryQuery,
} from "@/app/Invoices/GraphQL/receivablesReconciliationQuery";
import { Input } from "@/components/catalyst/input";
import { Button } from "@/components/catalyst/button";
import { Heading } from "@/components/catalyst/heading";
import { Divider } from "@/components/catalyst/divider";
import { Field, Label } from "@/components/catalyst/fieldset";
import {
  changeCurrencyToDollars,
  formatCurrency,
} from "@/lib/Formatters/formatCurrency";
import { Currency } from "@/gql/graphql";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
  themeQuartz,
  ProcessCellForExportParams,
} from "ag-grid-community";
import { setupAGGrid } from "@/lib/AGGrid/setupAGGrid";

setupAGGrid();

const imoovaTheme = themeQuartz.withParams({
  browserColorScheme: "light",
  fontFamily: {
    googleFont: "Inter",
  },
  fontSize: 12,
  headerFontSize: 14,
  spacing: 5,
});

const defaultColDef = {
  floatingFilter: true,
  sortable: true,
  resizable: true,
  flex: 1,
};

const sidebar = {
  toolPanels: ["columns", "filters"],
};

export function ReceivablesReconciliationReportScreen() {
  const gridRef = useRef<AgGridReact>(null);

  const [asOfDate, setAsOfDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });

  const { data: reconciliationData, isLoading: isLoadingReconciliation } =
    useGqlQuery(
      receivablesReconciliationQuery,
      { asOfDate },
      { enabled: !!asOfDate },
    );

  const { data: summaryData, isLoading: isLoadingSummary } = useGqlQuery(
    receivablesReconciliationSummaryQuery,
    { asOfDate },
    { enabled: !!asOfDate },
  );

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAsOfDate(event.target.value);
  };

  const handleExport = useCallback(
    (type: "csv" | "excel") => {
      const onlySelected = gridRef.current?.api.getSelectedRows().length !== 0;

      const processCellCallback = (
        params: ProcessCellForExportParams<ReceivablesReconciliationListItem>,
      ) => {
        const fieldName = params.column.getColDef().field;
        const currencyFields = [
          "total_invoiced",
          "total_payments",
          "total_credits",
          "total_refunds",
          "total_amount_due",
          "amount_due",
        ];

        if (
          fieldName &&
          currencyFields.includes(fieldName) &&
          params.node?.data?.currency &&
          params.value
        ) {
          return changeCurrencyToDollars(
            params.value,
            params.node?.data?.currency as Currency,
          );
        }

        return params.value;
      };

      if (type === "csv") {
        gridRef.current?.api.exportDataAsCsv({
          onlySelected,
          fileName: `receivables-reconciliation-${asOfDate}.csv`,
          processCellCallback,
        });
      } else {
        gridRef.current?.api.exportDataAsExcel({
          onlySelected,
          fileName: `receivables-reconciliation-${asOfDate}.xlsx`,
          processCellCallback,
        });
      }
    },
    [asOfDate],
  );

  const formatCurrencyAmount = useCallback((params: ValueFormatterParams) => {
    const currency = params.data?.currency;
    const value = params.value || 0;
    return formatCurrency(value, currency as Currency);
  }, []);

  const renderAmountDueCell = useCallback((params: ICellRendererParams) => {
    const amount = params.value || 0;
    const currency = params.data?.currency;
    const formattedAmount = formatCurrency(amount, currency as Currency);

    if (amount > 0) {
      return (
        <span className="font-semibold text-red-600">{formattedAmount}</span>
      );
    }

    return <span className="font-semibold">{formattedAmount}</span>;
  }, []);

  const summaryColumns = useMemo<ColDef[]>(
    () => [
      {
        field: "currency",
        headerName: "Currency",
        filter: "agSetColumnFilter",
        enableRowGroup: true,
        flex: 1,
        minWidth: 100,
      },
      {
        field: "account_count",
        headerName: "Account Count",
        filter: "agNumberColumnFilter",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "total_invoiced",
        headerName: "Total Invoiced",
        filter: "agNumberColumnFilter",
        valueFormatter: formatCurrencyAmount,
        cellClass: "text-right",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "total_payments",
        headerName: "Payments",
        filter: "agNumberColumnFilter",
        valueFormatter: formatCurrencyAmount,
        cellClass: "text-right",
        flex: 1,
        minWidth: 110,
      },
      {
        field: "total_credits",
        headerName: "Credits",
        filter: "agNumberColumnFilter",
        valueFormatter: formatCurrencyAmount,
        cellClass: "text-right",
        flex: 1,
        minWidth: 110,
      },
      {
        field: "total_refunds",
        headerName: "Refunds",
        filter: "agNumberColumnFilter",
        valueFormatter: formatCurrencyAmount,
        cellClass: "text-right",
        flex: 1,
        minWidth: 110,
      },
      {
        field: "total_amount_due",
        headerName: "Amount Due",
        filter: "agNumberColumnFilter",
        cellRenderer: renderAmountDueCell,
        cellClass: "text-right",
        flex: 1,
        minWidth: 130,
      },
    ],
    [formatCurrencyAmount, renderAmountDueCell],
  );

  const detailColumns = useMemo<ColDef[]>(
    () => [
      {
        field: "contact_name",
        headerName: "Contact Name",
        filter: "agTextColumnFilter",
        enableRowGroup: true,
        flex: 2,
        minWidth: 180,
        pinned: "left",
      },
      {
        field: "currency",
        headerName: "Currency",
        filter: "agSetColumnFilter",
        enableRowGroup: true,
        flex: 1,
        minWidth: 100,
      },
      {
        field: "total_invoiced",
        headerName: "Total Invoiced",
        filter: "agNumberColumnFilter",
        valueFormatter: formatCurrencyAmount,
        cellClass: "text-right",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "total_payments",
        headerName: "Payments",
        filter: "agNumberColumnFilter",
        valueFormatter: formatCurrencyAmount,
        cellClass: "text-right",
        flex: 1,
        minWidth: 110,
      },
      {
        field: "total_credits",
        headerName: "Credits",
        filter: "agNumberColumnFilter",
        valueFormatter: formatCurrencyAmount,
        cellClass: "text-right",
        flex: 1,
        minWidth: 110,
      },
      {
        field: "total_refunds",
        headerName: "Refunds",
        filter: "agNumberColumnFilter",
        valueFormatter: formatCurrencyAmount,
        cellClass: "text-right",
        flex: 1,
        minWidth: 110,
      },
      {
        field: "amount_due",
        headerName: "Amount Due",
        filter: "agNumberColumnFilter",
        cellRenderer: renderAmountDueCell,
        cellClass: "text-right",
        flex: 1,
        minWidth: 130,
      },
    ],
    [formatCurrencyAmount, renderAmountDueCell],
  );

  return (
    <FullPageScreen>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Heading>Receivables Reconciliation Report</Heading>
        </div>

        <Field className="max-w-sm">
          <Label>As of Date</Label>
          <Input type="date" value={asOfDate} onChange={handleDateChange} />
        </Field>

        {summaryData?.receivablesReconciliationSummary ? (
          <>
            <Heading level={2}>Summary</Heading>
            <div className="w-full">
              <AgGridReact
                rowData={summaryData.receivablesReconciliationSummary}
                columnDefs={summaryColumns}
                defaultColDef={defaultColDef}
                theme={imoovaTheme}
                loading={isLoadingSummary}
                enableCharts={false}
                suppressMenuHide={true}
                domLayout="autoHeight"
              />
            </div>
            <Divider />
          </>
        ) : null}

        <div className="flex items-center justify-between">
          <Heading level={2}>Receivables by Account</Heading>
          <div className="flex gap-2">
            <Button outline onClick={() => handleExport("csv")}>
              Export CSV
            </Button>
            <Button outline onClick={() => handleExport("excel")}>
              Export Excel
            </Button>
          </div>
        </div>

        <div className="w-full">
          <AgGridReact
            ref={gridRef}
            rowData={reconciliationData?.receivablesReconciliation ?? []}
            columnDefs={detailColumns}
            defaultColDef={defaultColDef}
            theme={imoovaTheme}
            loading={isLoadingReconciliation}
            enableCharts={true}
            cellSelection={true}
            sideBar={sidebar}
            domLayout="autoHeight"
            suppressMenuHide={true}
            overlayLoadingTemplate="Loading receivables data..."
            overlayNoRowsTemplate={
              asOfDate
                ? "No receivables found for the selected date."
                : "Please select a date to view receivables."
            }
          />
        </div>
      </div>
    </FullPageScreen>
  );
}
